import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, Clock, User, Search, Plus, 
  Stethoscope, Video, Phone, MapPin, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';

const CreateAppointmentDialog = ({ 
  open, 
  onClose, 
  onSuccess,
  preselectedMember = null 
}) => {
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Members search
  const [memberSearch, setMemberSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  
  // Providers and services
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  
  // Form state
  const [form, setForm] = useState({
    appointment_date: '',
    appointment_time: '',
    duration_minutes: 30,
    provider_id: '',
    service_id: '',
    appointment_type: 'general',
    appointment_mode: 'in_person',
    reason: '',
    notes: ''
  });

  // Load providers and services
  useEffect(() => {
    if (open) {
      loadProviders();
      loadServices();
      
      // If preselected member, set it
      if (preselectedMember) {
        setSelectedMember(preselectedMember);
      }
    }
  }, [open, preselectedMember]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setMemberSearch('');
      setSearchResults([]);
      setSelectedMember(preselectedMember || null);
      setForm({
        appointment_date: '',
        appointment_time: '',
        duration_minutes: 30,
        provider_id: '',
        service_id: '',
        appointment_type: 'general',
        appointment_mode: 'in_person',
        reason: '',
        notes: ''
      });
    }
  }, [open, preselectedMember]);

  const loadProviders = async () => {
    try {
      const response = await axios.get(`${API}/appointments/providers`);
      setProviders(response.data);
    } catch (error) {
      console.error('Failed to load providers:', error);
    }
  };

  const loadServices = async () => {
    try {
      const response = await axios.get(`${API}/appointments/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to load services:', error);
    }
  };

  // Search members with debounce
  useEffect(() => {
    const debounce = setTimeout(async () => {
      if (memberSearch.length >= 2) {
        setSearchLoading(true);
        try {
          const response = await axios.get(`${API}/search/members`, {
            params: { q: memberSearch, limit: 10 }
          });
          setSearchResults(response.data);
        } catch (error) {
          console.error('Search failed:', error);
          // Fallback to basic search
          try {
            const response = await axios.get(`${API}/members`);
            const filtered = response.data.filter(m => 
              m.full_name?.toLowerCase().includes(memberSearch.toLowerCase()) ||
              m.email?.toLowerCase().includes(memberSearch.toLowerCase()) ||
              m.patient_id?.toLowerCase().includes(memberSearch.toLowerCase())
            );
            setSearchResults(filtered.slice(0, 10));
          } catch (e) {
            console.error('Fallback search failed:', e);
          }
        } finally {
          setSearchLoading(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [memberSearch]);

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setMemberSearch('');
    setSearchResults([]);
  };

  const handleClearMember = () => {
    setSelectedMember(null);
    setMemberSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedMember) {
      toast.error('Please select a patient/member');
      return;
    }
    
    if (!form.appointment_date || !form.appointment_time) {
      toast.error('Please select date and time');
      return;
    }
    
    setLoading(true);
    try {
      const dateTime = new Date(`${form.appointment_date}T${form.appointment_time}`);
      
      const payload = {
        member_id: selectedMember.id,
        appointment_date: dateTime.toISOString(),
        duration_minutes: parseInt(form.duration_minutes),
        provider_id: form.provider_id || null,
        service_id: form.service_id || null,
        appointment_type: form.appointment_type,
        appointment_mode: form.appointment_mode,
        reason: form.reason || null,
        notes: form.notes || null
      };
      
      await axios.post(`${API}/appointments`, payload);
      
      toast.success('Appointment created successfully');
      onSuccess?.();
      onClose();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format provider name
  const formatProviderName = (provider) => {
    const titlePrefixes = ['Dr.', 'Dr', 'MD', 'DO', 'PhD', 'NP', 'PA', 'RN'];
    const name = provider.full_name || '';
    const startsWithTitle = titlePrefixes.some(p => name.toLowerCase().startsWith(p.toLowerCase()));
    
    if (startsWithTitle || !provider.title) {
      return name;
    }
    return `${provider.title} ${name}`;
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-teal-600" />
            Create New Appointment
          </DialogTitle>
          <DialogDescription>
            Schedule an appointment for a patient member
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Member Selection */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Patient/Member *</Label>
            
            {selectedMember ? (
              <Card className="border-teal-200 bg-teal-50">
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center text-white font-semibold">
                        {selectedMember.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{selectedMember.full_name}</p>
                        <p className="text-sm text-gray-600">{selectedMember.email}</p>
                        {selectedMember.patient_id && (
                          <p className="text-xs text-gray-500">ID: {selectedMember.patient_id}</p>
                        )}
                      </div>
                    </div>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={handleClearMember}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      Change
                    </Button>
                  </div>
                  
                  {selectedMember.membership_status === 'active' ? (
                    <Badge className="mt-2 bg-green-100 text-green-800">
                      Active Member - {selectedMember.membership_plan_name || 'Standard Plan'}
                    </Badge>
                  ) : (
                    <Badge className="mt-2 bg-yellow-100 text-yellow-800">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      No Active Membership
                    </Badge>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by name, email, or patient ID..."
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    className="pl-10"
                    data-testid="member-search-input"
                  />
                </div>
                
                {searchLoading && (
                  <div className="text-center py-2 text-gray-500 text-sm">
                    Searching...
                  </div>
                )}
                
                {searchResults.length > 0 && (
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {searchResults.map((member) => (
                      <div
                        key={member.id}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 flex justify-between items-center"
                        onClick={() => handleSelectMember(member)}
                        data-testid={`member-result-${member.id}`}
                      >
                        <div>
                          <p className="font-medium">{member.full_name}</p>
                          <p className="text-sm text-gray-500">{member.email}</p>
                        </div>
                        {member.membership_status === 'active' && (
                          <Badge className="bg-green-100 text-green-800 text-xs">Active</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {memberSearch.length >= 2 && searchResults.length === 0 && !searchLoading && (
                  <p className="text-sm text-gray-500 text-center py-2">
                    No members found matching &quot;{memberSearch}&quot;
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Date *
              </Label>
              <Input
                type="date"
                value={form.appointment_date}
                onChange={(e) => setForm({...form, appointment_date: e.target.value})}
                min={getMinDate()}
                required
                data-testid="appointment-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Time *
              </Label>
              <Input
                type="time"
                value={form.appointment_time}
                onChange={(e) => setForm({...form, appointment_time: e.target.value})}
                required
                data-testid="appointment-time"
              />
            </div>
          </div>

          {/* Duration and Mode */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Duration</Label>
              <Select 
                value={form.duration_minutes.toString()} 
                onValueChange={(v) => setForm({...form, duration_minutes: parseInt(v)})}
              >
                <SelectTrigger data-testid="duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">60 minutes</SelectItem>
                  <SelectItem value="90">90 minutes</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Appointment Mode</Label>
              <Select 
                value={form.appointment_mode} 
                onValueChange={(v) => setForm({...form, appointment_mode: v})}
              >
                <SelectTrigger data-testid="mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_person">
                    <span className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" /> In Person
                    </span>
                  </SelectItem>
                  <SelectItem value="telemedicine">
                    <span className="flex items-center gap-2">
                      <Video className="h-4 w-4" /> Telemedicine
                    </span>
                  </SelectItem>
                  <SelectItem value="phone">
                    <span className="flex items-center gap-2">
                      <Phone className="h-4 w-4" /> Phone Call
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Provider and Service */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Provider
              </Label>
              <Select 
                value={form.provider_id || 'none'} 
                onValueChange={(v) => setForm({...form, provider_id: v === 'none' ? '' : v})}
              >
                <SelectTrigger data-testid="provider-select">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Any available provider</SelectItem>
                  {providers.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {formatProviderName(p)}
                      {p.specialty && ` - ${p.specialty}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Stethoscope className="h-4 w-4" />
                Service
              </Label>
              <Select 
                value={form.service_id || 'none'} 
                onValueChange={(v) => setForm({...form, service_id: v === 'none' ? '' : v})}
              >
                <SelectTrigger data-testid="service-select">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">General appointment</SelectItem>
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                      {s.duration_minutes && ` (${s.duration_minutes} min)`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Appointment Type */}
          <div className="space-y-2">
            <Label>Appointment Type</Label>
            <Select 
              value={form.appointment_type} 
              onValueChange={(v) => setForm({...form, appointment_type: v})}
            >
              <SelectTrigger data-testid="type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="consultation">Consultation</SelectItem>
                <SelectItem value="follow_up">Follow Up</SelectItem>
                <SelectItem value="checkup">Checkup</SelectItem>
                <SelectItem value="procedure">Procedure</SelectItem>
                <SelectItem value="emergency">Emergency</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reason and Notes */}
          <div className="space-y-2">
            <Label>Reason for Visit</Label>
            <Input
              value={form.reason}
              onChange={(e) => setForm({...form, reason: e.target.value})}
              placeholder="Brief description of why the patient is visiting..."
              data-testid="reason-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Internal Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              placeholder="Additional notes for staff (not visible to patient)..."
              rows={3}
              data-testid="notes-input"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading || !selectedMember}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="create-appointment-btn"
            >
              {loading ? (
                <>Creating...</>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Appointment
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAppointmentDialog;
